import { CampaignAttributionSchema, parseCampaignAttribution, type CampaignAttribution } from '@packages/types';

/**
 * First-touch campaign attribution for signups.
 *
 * The landing page copies its campaign parameters onto every link into the app
 * (apps/web/bill-coming-soon/src/scripts/tracking.ts). The app keeps the first
 * set it sees and sends it with the signup request, so the signup events can be
 * tied to the campaign that brought the visitor. It lives in sessionStorage
 * because both signup paths stay in the tab: the email form submits from
 * /signup, and the OAuth round trip returns to /oauth/callback in the same tab.
 */

const STORAGE_KEY = 'signup_campaign_attribution';

/** Stores the campaign parameters in `search` unless a first touch is already stored. */
export function captureCampaignAttribution(search: string): void {
  const attribution = parseCampaignAttribution(search);
  if (!attribution) return;
  try {
    if (sessionStorage.getItem(STORAGE_KEY) !== null) return;
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(attribution));
  } catch {
    // Storage unavailable (private mode, quota): the signup goes unattributed.
  }
}

/** The stored first touch, or undefined when there is none or it does not parse. */
export function readCampaignAttribution(): CampaignAttribution | undefined {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (raw === null) return undefined;
    const parsed = CampaignAttributionSchema.safeParse(JSON.parse(raw));
    return parsed.success ? parsed.data : undefined;
  } catch {
    return undefined;
  }
}
